
import { lazy } from 'react';
import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { CartUrls } from './apps/efas/cart/Urls.tsx';
import { ComposeUrls } from './apps/efas/compose/Urls.tsx';
import FeedbackUrls from './apps/efas/feedbacks/Urls.tsx';
import { GlofasStaticUrls } from './apps/glofas/Urls.tsx';
import IS_EFAS from './components/sitebase/Environment.ts';
import { EFAS_ROUTES } from './efasRoutes.tsx';
import { GLOFAS_ROUTES } from './glofasRoutes.tsx';
import { InlineUrls } from './apps/efas/inline/Urls.tsx';

const PageNotFoundPage = lazy(() => import('./components/PageNotFoundPage.tsx'));
const EFASLandingPage = lazy(() => import('./components/sitebase/EFASLandingPage.tsx'));
const FeedbackReminderesApp = lazy(() => import('./apps/efas/feedback_reminders/FeedbackRemindersApp.tsx'));
const FloodlistApp = lazy(() => import('./apps/efas/floodlist/FloodlistApp.tsx'));
const FloodlistDetail = lazy(() => import('./apps/efas/floodlist/FloodlistDetail.tsx'));
const GLOFASLandingPage = lazy(() => import('./components/sitebase/GLOFASLandingPage.tsx'));
const MapAppFullScreen = lazy(() => import('./apps/map/MapV3.tsx'));
const NotificationsApp = lazy(() => import('./apps/efas/notifications/NotificationApp.tsx'));
const NotificationsList = lazy(() => import('./apps/efas/notifications/NotificationList.tsx'));
const SiteBase = lazy(() => import('./components/sitebase/SiteBase.tsx'));
const UserPage = lazy(() => import('./apps/user/UserPage.tsx'));
const NotificationReport = lazy(() => import('./apps/efas/report/NotificationReport.tsx'));
const NotificationSummaryMap = lazy(() => import('./apps/efas/notifications/NotificationSummaryMap.tsx'));
const DowntimePage = lazy(() => import('./apps/service-status/DowntimePage.tsx'));


const routes: RouteObject[] = [
    {
        // React iframes
        path: '/if/',
        children: [
            {
                path: 'service-status',
                children: [
                    {
                        path: 'efas',
                        lazy: async () => {
                            const { ECMWFServiceStatus } = await import('./apps/service-status/ECMWFServiceStatus.tsx');
                            return { Component: ECMWFServiceStatus };
                        }
                    },
                    {
                        path: 'gfm',
                        lazy: async () => {
                            const { GFMServiceStatus } = await import('./apps/service-status/GFMServiceStatus.tsx');
                            return { Component: GFMServiceStatus };
                        }
                    },
                ]
            }
        ]
    },
    {
        path: '/map',
        element: <SiteBase fullScreen={true} />,
        children: [
            {
                index: true,
                element: <MapAppFullScreen />
            },
        ],
    },
    {
        path: '/downtime',
        element: <DowntimePage />,
    },
    {
        path: '/',
        element: <SiteBase landingPage={true} />,
        errorElement: <SiteBase landingPage={true}><PageNotFoundPage /></SiteBase>,
        children: [
            {
                index: true,
                element: IS_EFAS ? <EFASLandingPage /> : <GLOFASLandingPage />
            },
        ]
    },
    {
        path: '/',
        element: <SiteBase landingPage={true} requireLogin={true} />,
        children: [
            {
                path: "inline",
                children: InlineUrls
            },
        ]
    },
    {
        path: '/',
        element: <SiteBase />,
        children: IS_EFAS ? EFAS_ROUTES : GLOFAS_ROUTES
    },
    {
        path: '/',
        element: <SiteBase />,
        children: GlofasStaticUrls
    },
    {
        path: '/',
        element: <SiteBase />,
        children: [
            {
                path: "notifications/summary",
                element: <NotificationSummaryMap />,
            },
        ]
    },
    {
        path: "/",
        element: <SiteBase requireLogin={true} />,
        children: [
            {
                path: "feedback-reminders",
                children: [
                    {
                        index: true,
                        element: <FeedbackReminderesApp />
                    },
                    {
                        path: ":feedback_reminder_id",
                        lazy: async () => {
                            const { FeedbackRemindersDetail } = await import('./apps/efas/feedback_reminders/FeedbackRemindersDetail.tsx');
                            return { Component: FeedbackRemindersDetail }
                        },
                        loader: async ({ params }) => {
                            return fetch(`/api/feedback-reminders/${params.feedback_reminder_id}`);
                        },
                    }
                ]
            },
            {
                path: "feedbacks",
                children: FeedbackUrls
            },
            {
                path: "cart",
                children: CartUrls
            },
            {
                path: "cart/compose",
                children: ComposeUrls
            },
            {
                path: "rpcounter",
                lazy: async () => {
                    const { RpCounter } = await import('./apps/efas/rpcounter/RpCounter.tsx');
                    return { Component: RpCounter };
                }
            },
            {
                path: "user",
                element: <UserPage />
            },
            {
                path: "profiles/user-profile",
                element: <UserPage />
            },
            {
                path: "notifications-archive",
                children: [
                    {
                        index: true,
                        element: <NotificationsApp />,
                    },
                    {
                        path: "formal",
                        element: <NotificationsList type={1} />,
                    },
                    {
                        path: "informal",
                        element: <NotificationsList type={2} />
                    },
                    {
                        path: "flash",
                        element: <NotificationsList type={3} />
                    },
                    {
                        path: "ercc",
                        element: <NotificationsList type={4} />
                    },
                    {
                        path: "detail",
                        children: [
                            {
                                path: ":notification_id",
                                lazy: async () => {
                                    const { NotificationDetail } = await import('./apps/efas/notifications/NotificationDetail.tsx');
                                    return { Component: NotificationDetail }
                                },
                                loader: async ({ params }) => {
                                    return fetch(`/api/notifications/sent/?notification_id=${params.notification_id}`);
                                },
                            },
                            {
                                path: "ercc/:id",
                                lazy: async () => {
                                    const { ErccDetail } = await import('./apps/efas/notifications/ErccDetail.tsx');
                                    return { Component: ErccDetail }
                                },
                                loader: async ({ params }) => {
                                    return fetch(`/api/ercc/${params.id}`);
                                },
                            }
                        ]
                    }
                ]
            },
            {
                path: "notifications-report",
                element: <NotificationReport />,
            },
            {
                path: "efas-formal-flood-notification",
                element: <NotificationsList type={1} />,
            },
            {
                path: "efas-informal-flood-notification",
                element: <NotificationsList type={2} />,
            },
            {
                path: "efas-flash-flood-notification",
                element: <NotificationsList type={3} />,
            },
            {
                path: "efas-ercc-overview",
                element: <NotificationsList type={4} />,
            },
            {
                path: "floodlist",
                children: [
                    {
                        index: true,
                        element: <FloodlistApp />
                    },
                    {
                        path: "",
                        children: [
                            {
                                path: ":alert_id",
                                element: <FloodlistDetail />,
                                loader: async ({ params }) => {
                                    return fetch(`/api/floodlist/alerts/${params.alert_id}`);
                                },
                            }
                        ]
                    }
                ]
            },
            {
                path: "messages",
                children: [
                    {
                        index: true,
                        lazy: async () => {
                            const { MessagesApp } = await import('./apps/messages/MessagesApp.tsx');
                            return { Component: MessagesApp };
                        }
                    },
                    {
                        path: ":message_id",
                        lazy: async () => {
                            const { MessageDetail } = await import('./apps/messages/MessageDetail.tsx');
                            return { Component: MessageDetail };
                        },

                    }
                ]
            }
        ]
    }
];

export const router = createBrowserRouter(routes);